import React, { useState, useEffect, useMemo } from 'react';
import { useParams, useNavigate, useLocation, Link } from 'react-router-dom';
import { Project, Transaction, PaginatedResult, ProjectPerson } from '../types';
import { projectsAPI, projectPersonsAPI, transactionsAPI } from '../services/api';
import TransactionsTable from './TransactionsTable';
import Pagination from './Pagination';
import { usePagination } from '../hooks/usePagination';
import ActivitiesList from './ActivitiesList';
import { BarChart, Bar, XAxis, YAxis, CartesianGrid, Tooltip, Legend, ResponsiveContainer, Cell } from 'recharts';
import { ProjectType } from '../utils/constants';
import ReactMarkdown from 'react-markdown';
import { addRedirectToUrl } from '../utils/redirectUtils';
import { formatDate } from '../utils/dateFormat';
import { useConfirm } from '../context/ConfirmContext';

const ProjectDetails: React.FC = () => {
  const { id } = useParams<{ id: string }>();
  // Reject partial numeric routes instead of letting requests and identity checks disagree.
  const parsedProjectId = Number(id);
  const projectId = id && /^\d+$/.test(id) && Number.isSafeInteger(parsedProjectId) && parsedProjectId > 0
    ? parsedProjectId
    : null;
  const navigate = useNavigate();
  const location = useLocation();
  const [project, setProject] = useState<Project | null>(null);
  const [transactions, setTransactions] = useState<Transaction[]>([]);
  const [currentPage, setCurrentPage] = useState(0);
  const [pageSize, setPageSize] = useState(10);
  const [projectPersons, setProjectPersons] = useState<ProjectPerson[]>([]);
  const [loading, setLoading] = useState(true);
  const [resolvedRouteId, setResolvedRouteId] = useState<string>();
  const [transactionsLoading, setTransactionsLoading] = useState(true);
  const [transactionsError, setTransactionsError] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [exporting, setExporting] = useState(false);
  const [exportError, setExportError] = useState<string | null>(null);
  const confirm = useConfirm();

  useEffect(() => {
    if (projectId === null) return;
    let active = true;
    const loadProjectDetails = async () => {
      setLoading(true);
      setError(null);
      try {
        const [projectData, personsData] = await Promise.all([
          projectsAPI.getProject(projectId),
          projectPersonsAPI.getProjectPersonsByProject(projectId)
        ]);
        if (!active) return;
        setProject(projectData);
        setProjectPersons(personsData);
      } catch (err) {
        if (!active) return;
        setProject(null);
        setError('Failed to load project details');
        console.error('Error loading project details:', err);
      } finally {
        if (active) {
          setResolvedRouteId(id);
          setLoading(false);
        }
      }
    };
    void loadProjectDetails();
    return () => { active = false; };
  }, [id, projectId]);

  const loadTransactions = async () => {
    if (projectId === null) return;

    try {
      setTransactionsLoading(true);
      setTransactionsError(false);
      const transactionsData = await projectsAPI.getProjectTransactions(projectId, {
        pageNumber: 1,
        pageSize: 1000,
        sortBy: 'date',
        sortDescending: true
      });
      setTransactions(transactionsData.items ?? []);
    } catch (err) {
      setTransactionsError(true);
      console.error('Error loading project transactions:', err);
    } finally {
      setTransactionsLoading(false);
    }
  };

  useEffect(() => {
    loadTransactions();
  }, [projectId]);

  const handleExportTransactions = async () => {
    if (!project || exporting) return;

    setExporting(true);
    setExportError(null);
    try {
      const workbook = await projectsAPI.exportProjectTransactions(project.id);
      const url = URL.createObjectURL(workbook);
      const link = document.createElement('a');
      try {
        link.href = url;
        link.download = `project-${project.id}-transactions.xlsx`;
        document.body.appendChild(link);
        link.click();
      } finally {
        link.remove();
        URL.revokeObjectURL(url);
      }
    } catch {
      setExportError('Failed to export transactions. Please try again.');
    } finally {
      setExporting(false);
    }
  };

  const handlePageChange = (page: number) => {
    setCurrentPage(page);
  };

  const handlePageSizeChange = (size: number) => {
    setPageSize(size);
    setCurrentPage(0);
  };

  const handleEditTransaction = (transaction: Transaction) => {
    navigate(addRedirectToUrl(`/transactions?editId=${transaction.id}`, location.pathname + location.search));
  };

  const handleCopyTransaction = (transaction: Transaction) => {
    navigate(addRedirectToUrl(`/transactions/create?copyId=${transaction.id}`, location.pathname + location.search));
  };

  const handleDeleteTransaction = async (transactionId: number) => {
    const isConfirmed = await confirm({
      title: 'Delete transaction',
      message: 'Are you sure you want to delete this transaction? This action cannot be undone.',
      confirmText: 'Delete',
      cancelText: 'Cancel',
      variant: 'danger'
    });

    if (!isConfirmed) return;

    try {
      await transactionsAPI.deleteTransaction(transactionId);
      await loadTransactions();
    } catch (err) {
      console.error('Error deleting transaction:', err);
    }
  };

  const getStatusText = (statusId: number) => {
    switch (statusId) {
      case 1: return 'Request';
      case 2: return 'Canceled';
      case 3: return 'In Progress';
      case 4: return 'Completed';
      case 5: return 'Support';
      case 6: return 'Lead';
      case 7: return 'Opportunity';
      case 8: return 'Rejected';
      case 9: return 'Follow-up';
      case 10: return 'Confirmed';
      case 11: return 'Maintenance';
      case 12: return 'Lost';
      case 13: return 'Internal';
      default: return 'Unknown';
    }
  };

  const getStatusBadgeColor = (statusId: number) => {
    switch (statusId) {
      case 1:
        return '#6b7280';
      case 2:
        return '#52525b';
      case 3:
        return '#4b5563';
      case 4:
        return '#16a34a';
      case 5:
        return '#a1a1aa';
      case 6:
        return '#6b7280';
      case 7:
        return '#111111';
      case 8:
        return '#6b7280';
      case 9:
        return '#2563eb';
      case 10:
        return '#15803d';
      case 11:
        return '#334155';
      case 12:
        return '#dc2626';
      case 13:
        return '#4b5563';
      default:
        return '#6b7280';
    }
  };

  const getCategoryText = (category?: number) => {
    if (!category) return '-';
    switch (category) {
      case 1: return 'Development';
      case 2: return 'Design';
      case 3: return 'Marketing';
      case 4: return 'Support';
      default: return 'Other';
    }
  };

  const calculateTotalAmount = () => {
    return transactions.reduce((sum: number, transaction: Transaction) => sum + transaction.totalAmount, 0);
  };

  const formatCurrencyLocal = (amount: number) => {
    return new Intl.NumberFormat('en-US', {
      style: 'currency',
      currency: 'USD',
    }).format(amount);
  };

  const completedTransactions = useMemo(() => (
    transactions.filter(t => t.isCompleted)
  ), [transactions]);

  const totalDebt = useMemo(() => transactions.reduce((sum, transaction) => {
    if (transaction.isCompleted || transaction.isProcessed ||
        (transaction.sourceId !== null && transaction.sourceId !== undefined)) {
      return sum;
    }
    return sum + transaction.totalAmount;
  }, 0), [transactions]);

  const profitLossData = useMemo(() => {
    const items = completedTransactions;
    if (items.length === 0) {
      return { income: 0, expense: 0, profit: 0, expensePct: 0, profitPct: 0 };
    }

    // Only completed transactions
    // Calculate based on typeId: 1 = Income, 2 = Expense
    // Also consider the sign of totalAmount (negative amounts are expenses)
    let income = 0;
    let expense = 0;

    items.forEach(t => {
      if (t.typeId === ProjectType.Income && t.totalAmount >= 0) {
        income += t.totalAmount;
      } else if (t.typeId === ProjectType.Expense || t.totalAmount < 0) {
        expense += Math.abs(t.totalAmount);
      }
    });

    const profit = income - expense;
    const expensePct = income > 0 ? (expense / income) * 100 : 0;
    const profitPct = income > 0 ? (profit / income) * 100 : 0;

    return {
      income,
      expense,
      profit,
      expensePct,
      profitPct
    };
  }, [completedTransactions]);

  // Hide the old project's rows/actions synchronously, before the new route effect runs.
  if (projectId !== null && (loading || resolvedRouteId !== id || (project && project.id !== projectId))) {
    return (
      <div className="container-fluid">
        <div className="d-flex flex-column justify-content-center align-items-center" style={{ minHeight: '60vh' }}>
          <div className="spinner-border" role="status" />
          <span className="visually-hidden">Loading...</span>
        </div>
      </div>
    );
  }

  if (projectId === null || error || !project) {
    return (
      <div className="container-fluid">
        <div className="alert alert-danger" role="alert">
          {projectId === null ? 'Project not found' : error || 'Project not found'}
        </div>
        <button className="btn btn-secondary" onClick={() => navigate(-1)}>
          Back
        </button>
      </div>
    );
  }

  return (
    <div className="container-fluid project-details-page">
      <div className="row mb-4 project-details-header">
        
        <div className="col-md-8">
          <h2 className="project-details-title">{project.title}</h2>
          <div className="d-flex flex-wrap gap-3 align-items-center">
            <span><strong>ID:</strong> {project.id}</span>
            <span>
              <strong>Customer:</strong>{' '}
              <Link
                to={`/customers/${project.customerId}`}
                className="text-decoration-underline"
                style={{ color: 'var(--primary)', textDecorationColor: 'var(--primary)', fontWeight: 600 }}
              >
                {project.customerTitle}
              </Link>
            </span>
            <span><strong>Date:</strong> {formatDate(project.date)}</span>
            {project.po && <span><strong>PO:</strong> {project.po}</span>}
            {project.isDayOffProject && (
              <span>
                <strong>Type:</strong>{' '}
                <span className="badge contact-type-badge" style={{ backgroundColor: '#2563eb', color: '#fff' }}>
                  Day off project
                </span>
              </span>
            )}
            <span>
              <strong>Status:</strong>{' '}
              <span
                className="badge contact-type-badge"
                style={{ backgroundColor: getStatusBadgeColor(project.statusId), color: '#fff' }}
              >
                {getStatusText(project.statusId)}
              </span>
            </span>
          </div>
        </div>
        <div className="col-md-4">
          <div className="d-flex flex-wrap justify-content-end gap-2 project-details-actions mb-3">
            <button
              className="btn btn-secondary"
              onClick={handleExportTransactions}
              disabled={exporting}
              aria-busy={exporting}
            >
              {exporting ? 'Exporting…' : 'Export to Excel'}
            </button>
            <button
              className="btn btn-primary"
              onClick={() => navigate(addRedirectToUrl(`/transactions/create?projectId=${project.id}`, location.pathname + location.search))}
            >
              Create Transaction
            </button>
            <button
              className="btn btn-primary"
              onClick={() => navigate(addRedirectToUrl(`/projects/edit/${project.id}`, location.pathname + location.search))}
            >
              Edit
            </button>
            <button
              className="btn btn-secondary"
              onClick={() => navigate(-1)}
            >
              Back
            </button>
          </div>
        </div>
      </div>

      {exportError && (
        <div className="alert alert-danger" role="alert">{exportError}</div>
      )}

      {/* Revenue, expenses, and profit use completed transactions; debt uses eligible pending transactions. */}
      <div className="grid grid-cols-1 md:grid-cols-2 xl:grid-cols-5 gap-4" style={{ marginBottom: '1rem' }}>
        <div>
          <div className="stats-card">
            <div className="stats-value">{formatCurrencyLocal(profitLossData.income)}</div>
            <div className="stats-label">Revenue</div>
          </div>
        </div>
        <div>
          <div className="stats-card">
            <div className="stats-value">{formatCurrencyLocal(profitLossData.expense)}</div>
            <div className="stats-label">Expenses ({profitLossData.expensePct.toFixed(0)}%)</div>
          </div>
        </div>
        <div>
          <div className="stats-card">
            <div className="stats-value">{formatCurrencyLocal(profitLossData.profit)}</div>
            <div className="stats-label">Profit ({profitLossData.profitPct.toFixed(0)}%)</div>
          </div>
        </div>
        <div>
          <div className="stats-card">
            <div className="stats-value">{completedTransactions.length}/{transactions.length}</div>
            <div className="stats-label">Completed</div>
          </div>
        </div>
        <div>
          <div className="stats-card" role="status" aria-label="Total debt">
            <div className="stats-value">
              {transactionsLoading ? 'Loading…' : transactionsError ? 'Unavailable' : formatCurrencyLocal(totalDebt)}
            </div>
            <div className="stats-label">Total debt</div>
          </div>
        </div>
      </div>

      <div className="row">
        <div className="col-12">
          {project.description && (
            <div className="card mb-4 project-details-card">
              <div className="card-header">
                <h5 className="mb-0">Description</h5>
              </div>
              <div className="card-body markdown-content">
                <ReactMarkdown>{project.description}</ReactMarkdown>
              </div>
            </div>
          )}

          {/* Assigned Persons Section */}
          {projectPersons.length > 0 && (
            <div className="card mb-4 project-details-card">
              <div className="card-header">
                <h5 className="mb-0">Assigned Persons</h5>
              </div>
              <div className="card-body">
                  <div className="row">
                  {projectPersons.map((pp) => (
                    <div key={pp.id} className="col-md-4 mb-3">
                      <div className="d-flex align-items-center p-3 border rounded">
                        <div className="flex-grow-1">
                          <h6 className="mb-1">
                            {pp.personFirstName} {pp.personLastName}
                          </h6>
                          {pp.role && (
                            <small className="text-muted">{pp.role}</small>
                          )}
                  </div>
                  
                      </div>
                    </div>
                  ))}
                </div>
              </div>
            </div>
          )}
        </div>
      </div>

      {/* Profit/Loss Chart removed (task 12) */}

      {transactionsLoading && (
        <div className="row">
          <div className="col-12">
            <div className="card project-details-card">
              <div className="card-body d-flex justify-content-center align-items-center gap-3 py-4" role="status" aria-label="Loading transactions">
                <div className="spinner-border" aria-hidden="true" />
                <span>Loading transactions…</span>
              </div>
            </div>
          </div>
        </div>
      )}

      {!transactionsLoading && transactions.length > 0 && (
        <div className="row">
          <div className="col-12">
            <div className="card project-details-card">
              <div className="card-header">
                <h5 className="mb-0">Transactions</h5>
              </div>
              <div className="card-body">
                <TransactionsTable
                  transactions={transactions.slice(currentPage * pageSize, (currentPage + 1) * pageSize)}
                  hideProject={true}
                  hideDescription={true}
                  amountDisplay="original"
                  rowColorMode="completedSign"
                  showActions={true}
                  onEdit={handleEditTransaction}
                  onCopy={handleCopyTransaction}
                  onDelete={handleDeleteTransaction}
                  highlightPendingStatus={true}
                  pendingStatusColor="#111111"
                  statusBadgeClassName="badge contact-type-badge"
                  showDirectionBadge={true}
                  directionBadgeColor="#4b5563"
                  sourceBadgeColor="#6b7280"
                />
                <Pagination
                  currentPage={currentPage}
                  totalItems={transactions.length}
                  itemsPerPage={pageSize}
                  onPageChange={setCurrentPage}
                  onPageSizeChange={setPageSize}
                  pageSizeOptions={[5, 10, 25, 50]}
                />
              </div>
            </div>
          </div>
        </div>
      )}

      {!transactionsLoading && transactions.length === 0 && (
        <div className="row">
          <div className="col-12">
            <div className="card project-details-card">
              <div className="card-body text-center">
                <p className="text-muted mb-3">No transactions found for this project.</p>
                <button
                  className="btn btn-success"
                  onClick={() => navigate(addRedirectToUrl(`/transactions/create?projectId=${project.id}`, location.pathname + location.search))}
                >
                  Create First Transaction
                </button>
              </div>
            </div>
          </div>
        </div>
      )}

      {project && (
        <div className="mt-4">
          <ActivitiesList key={`Project:${project.id}`} referenceType="Project" referenceId={project.id} />
        </div>
      )}
    </div>
  );
};

export default ProjectDetails;
